import {fromEvent, Subject, merge} from 'rxjs';
import {map, startWith, shareReplay, distinctUntilChanged, scan} from 'rxjs/operators';
import type {PlaceOrderSide} from '@px/enum';
import {compactItems, polyfillGridItems} from '@px/calc/grid-layout';
import {defaultGridLayoutBreakpoints, readGridLayoutBreakpoint, writeGridLayoutBreakpoint} from '@px/storage/grid-layout';

type GridLayoutBreakpointReduceAction = ReduceAction<GridLayoutBreakpoint>;

export const newGridItems$ = new Subject<GridItem[]>();
export const resetGridLayoutBreakpoint$ = new Subject<PlaceOrderSide>();
export const updateViewVisible$ = new Subject<{id: string; visible: boolean}>();

const gridLayoutRange$ = createGridLayoutRange$(window);

const updateRangeAction$ = gridLayoutRange$.pipe(
  map((range: number): GridLayoutBreakpointReduceAction => {
    return function (oldBreakpoint: GridLayoutBreakpoint): GridLayoutBreakpoint {
      return oldBreakpoint && oldBreakpoint.range === range ? oldBreakpoint : readGridLayoutBreakpoint(range);
    };
  })
);

const updateItemsAction$ = newGridItems$.pipe(
  map((items: GridItem[]): GridLayoutBreakpointReduceAction => {
    return function (oldBreakpoint: GridLayoutBreakpoint): GridLayoutBreakpoint {
      const {range, cols} = oldBreakpoint;
      return {range, cols, items};
    };
  })
);

const resetGridLayoutAction$ = resetGridLayoutBreakpoint$.pipe(
  map((placeOrderSide): GridLayoutBreakpointReduceAction => {
    return function (oldBreakpoint: GridLayoutBreakpoint): GridLayoutBreakpoint {
      const gridLayoutBreakpoint = defaultGridLayoutBreakpoints.find((o) => o.range === oldBreakpoint.range);
      if (gridLayoutBreakpoint) {
        const {range, cols} = oldBreakpoint;
        return {range, cols, items: compactItems(polyfillGridItems(gridLayoutBreakpoint.createDefaultItems(placeOrderSide)))};
      }
      return oldBreakpoint;
    };
  })
);

const updateViewVisibleAction$ = updateViewVisible$.pipe(
  map(({id, visible}): GridLayoutBreakpointReduceAction => {
    return function (oldBreakpoint: GridLayoutBreakpoint): GridLayoutBreakpoint {
      const {range, cols, items} = oldBreakpoint;
      return {range, cols, items: compactItems(updateViewVisible(items, id, visible))};
    };
  })
);

export const gridLayoutBreakpoint$ = merge<[GridLayoutBreakpointReduceAction, GridLayoutBreakpointReduceAction, GridLayoutBreakpointReduceAction, GridLayoutBreakpointReduceAction]>(
  updateRangeAction$,
  updateItemsAction$,
  resetGridLayoutAction$,
  updateViewVisibleAction$
).pipe(
  scan((p: GridLayoutBreakpoint, action: GridLayoutBreakpointReduceAction): GridLayoutBreakpoint => action(p), null),
  distinctUntilChanged(),
  shareReplay()
);

export const isMobileLayout$ = gridLayoutBreakpoint$.pipe(map((gridLayoutBreakpoint) => gridLayoutBreakpoint.range <= defaultGridLayoutBreakpoints[0].range));

gridLayoutBreakpoint$.subscribe((gridLayoutBreakpoint) => writeGridLayoutBreakpoint(gridLayoutBreakpoint));

function createGridLayoutRange$(window: Window) {
  return fromEvent(window, 'resize').pipe(
    startWith(0),
    map(() => calcWidth(window)),
    distinctUntilChanged(),
    map((w) => calcWidthRange(w)),
    distinctUntilChanged()
  );
}

function calcWidth(window: Window): number {
  return window.innerWidth;
}

function calcWidthRange(width): number {
  const max = defaultGridLayoutBreakpoints.slice(-1)[0].range;
  if (width > max) {
    return max;
  }
  return defaultGridLayoutBreakpoints.find((o) => width <= o.range).range;
}

function updateViewVisible(items: GridItem[], viewId: string, visible: boolean) {
  return items.map((item) => (item.id === viewId ? {...item, visible} : item));
}
